import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X, ArrowLeft, Upload, Undo, Redo } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditResourceBudgetForm } from './edit-resource-budget-form';
import { ImportBudgetDialog, type ImportPreview } from './import-budget-dialog';
import { BudgetExportModal } from './budget-export-modal';
import { Download } from 'lucide-react';
import type { BudgetData } from '@/types/budget.types';
import { toast } from 'sonner';

interface EditResourceBudgetPageProps {
  projectId?: number;
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  onSave: (updatedBudget: Partial<BudgetData>) => Promise<void>;
  onCancel: () => void;
}

export function EditResourceBudgetPage({
  projectId,
  budgetData,
  projectStartDate,
  projectEndDate,
  onSave,
  onCancel,
}: EditResourceBudgetPageProps) {
  const currentYear = new Date().getFullYear();
  
  // Calculate years based on project dates
  const years = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Fallback to default range if no project dates
      return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }
    
    const startYear = new Date(projectStartDate).getFullYear();
    const endYear = new Date(projectEndDate).getFullYear();
    
    // Generate array of years from start to end
    const yearRange: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      yearRange.push(year);
    }
    
    return yearRange;
  })();
  
  // Set initial selected year to the first year in the range
  const [selectedYear, setSelectedYear] = useState<string>(years[0]?.toString() || currentYear.toString());
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resourceBudgetChanges, setResourceBudgetChanges] = useState<any>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0); // Used to force form remount on undo/redo
  const isUndoRedoInProgress = useRef(false); // Flag to prevent history updates during undo/redo

  // Handle year change
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    
    const yearNum = parseInt(year);
    const currentYearNum = new Date().getFullYear();
    
    // Create new budget data based on selected year
    const updatedBudgetData: BudgetData = {
      ...budgetData,
      resourceBudget: {
        ...budgetData.resourceBudget,
        byRole: budgetData.resourceBudget.byRole.map(role => {
          // If selecting current year (2026), use the original data
          if (yearNum === currentYearNum) {
            return role;
          }
          
          // If selecting a future year (2027+), show empty data
          if (yearNum > currentYearNum) {
            return {
              ...role,
              budgetHours: 0,
              budgetCost: 0,
              monthlyHours: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            };
          }
          
          // If selecting a past year (2025 or earlier), show empty data
          return {
            ...role,
            budgetHours: 0,
            budgetCost: 0,
            monthlyHours: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          };
        }),
      },
    };
    
    setCurrentBudgetData(updatedBudgetData);
    setIsDirty(false); // Reset dirty state when changing years
    setResourceBudgetChanges(null); // Clear any pending changes
    setHasUserInteracted(false); // Reset interaction flag when changing years
    
    // Reset undo/redo history when changing years
    setHistory([]);
    setHistoryIndex(-1);
    
    console.log(`[EditResourceBudgetPage] Year changed to: ${year}`);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updatedBudget: Partial<BudgetData> = {};
      
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      await onSave(updatedBudget);
      
      // Reset state
      setIsDirty(false);
      setResourceBudgetChanges(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      // Set flag to prevent onChange from updating history during undo
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setResourceBudgetChanges(previousState);
      // Update currentBudgetData so the form reflects the undone state
      setCurrentBudgetData(prev => ({
        ...prev,
        resourceBudget: previousState,
      }));
      setIsDirty(newIndex > 0); // Only dirty if not back to initial state
      // Increment version to force form remount with new state
      setUndoRedoVersion(v => v + 1);
      
      // Reset flag after a short delay to allow form to remount
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      // Set flag to prevent onChange from updating history during redo
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setResourceBudgetChanges(nextState);
      // Update currentBudgetData so the form reflects the redone state
      setCurrentBudgetData(prev => ({
        ...prev,
        resourceBudget: nextState,
      }));
      setIsDirty(true);
      // Increment version to force form remount with new state
      setUndoRedoVersion(v => v + 1);
      
      // Reset flag after a short delay to allow form to remount
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);


  const handleImport = async (importData: ImportPreview, replaceMode: boolean) => {
    try {
      // Switch to the import year if different
      if (importData.year.toString() !== selectedYear) {
        setSelectedYear(importData.year.toString());
      }

      // Create updated budget data
      const updatedBudgetData = { ...currentBudgetData };
      
      if (replaceMode) {
        // Replace mode: Replace all roles for the year
        updatedBudgetData.resourceBudget = {
          ...updatedBudgetData.resourceBudget,
          byRole: importData.roles.map(importRole => {
            // Find existing role to get the rate
            const existingRole = budgetData.resourceBudget.byRole.find(
              r => r.role.toLowerCase() === importRole.role.toLowerCase()
            );
            
            const totalHours = importRole.totalHours;
            const billRate = existingRole?.billRate || 150; // Default rate if not found
            
            return {
              id: existingRole?.id || -Date.now(),
              role: importRole.role,
              budgetHours: totalHours,
              allocatedHours: existingRole?.allocatedHours || 0,
              actualHours: existingRole?.actualHours || 0,
              remainingHours: totalHours - (existingRole?.actualHours || 0),
              percentUsed: totalHours > 0 ? ((existingRole?.actualHours || 0) / totalHours) * 100 : 0,
              billRate,
              budgetCost: totalHours * billRate,
              actualCost: (existingRole?.actualHours || 0) * billRate,
              monthlyHours: importRole.monthlyHours,
            };
          }),
        };
      } else {
        // Additive mode: Update existing roles and add new ones
        const updatedRoles = [...updatedBudgetData.resourceBudget.byRole];
        
        importData.roles.forEach(importRole => {
          const existingIndex = updatedRoles.findIndex(
            r => r.role.toLowerCase() === importRole.role.toLowerCase()
          );
          
          const totalHours = importRole.totalHours;
          
          if (existingIndex >= 0) {
            // Update existing role
            const existingRole = updatedRoles[existingIndex];
            const billRate = existingRole.billRate;
            
            updatedRoles[existingIndex] = {
              ...existingRole,
              budgetHours: totalHours,
              monthlyHours: importRole.monthlyHours,
              remainingHours: totalHours - existingRole.actualHours,
              percentUsed: totalHours > 0 ? (existingRole.actualHours / totalHours) * 100 : 0,
              budgetCost: totalHours * billRate,
            };
          } else {
            // Add new role
            const billRate = 150; // Default rate for new roles
            
            updatedRoles.push({
              id: -Date.now(),
              role: importRole.role,
              budgetHours: totalHours,
              allocatedHours: 0,
              actualHours: 0,
              remainingHours: totalHours,
              percentUsed: 0,
              billRate,
              budgetCost: totalHours * billRate,
              actualCost: 0,
              monthlyHours: importRole.monthlyHours,
            });
          }
        });
        
        updatedBudgetData.resourceBudget = {
          ...updatedBudgetData.resourceBudget,
          byRole: updatedRoles,
        };
      }

      // Update the current budget data and mark as dirty
      setCurrentBudgetData(updatedBudgetData);
      setResourceBudgetChanges(updatedBudgetData.resourceBudget);
      setHasUserInteracted(true); // Import counts as user interaction
      setIsDirty(true);

      // Show success message
      const newCount = importData.roles.filter(r => r.isNew).length;
      const updateCount = importData.roles.filter(r => !r.isNew).length;
      
      let message = `Successfully imported budget for ${importData.year}. `;
      if (newCount > 0) message += `${newCount} new role${newCount !== 1 ? 's' : ''} added. `;
      if (updateCount > 0) message += `${updateCount} role${updateCount !== 1 ? 's' : ''} updated.`;
      
      toast.success('Import Successful', {
        description: message,
      });
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import Failed', {
        description: error instanceof Error ? error.message : 'An error occurred during import',
      });
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Edit Resource Budget</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Update budget values for {currentBudgetData.projectName}
                </p>
              </div>
              
              {/* Compact Unsaved Changes Indicator - inline in header */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>
            
            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExportDialogOpen(true)}
                disabled={isSaving || !currentBudgetData.resourceBudget.byRole || currentBudgetData.resourceBudget.byRole.length === 0}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportDialogOpen(true)}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4">
          <EditResourceBudgetForm
            key={`${selectedYear}-${undoRedoVersion}`}
            projectId={projectId ?? 0}
            resourceBudget={currentBudgetData.resourceBudget}
            selectedYear={selectedYear}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            onChange={(changes) => {
              setResourceBudgetChanges(changes);
              
              // Skip history updates during undo/redo operations
              // This prevents the form remount from re-adding the state to history
              if (isUndoRedoInProgress.current) {
                return;
              }
              
              // First onChange call - initialize history with the initial state
              if (!hasUserInteracted) {
                setHasUserInteracted(true);
                // Save the initial state as history[0] so we can undo back to it
                setHistory([currentBudgetData.resourceBudget]);
                setHistoryIndex(0);
                // Don't mark as dirty yet - this might be a mount call, not a user change
                // The next onChange call will be the actual user change
                return;
              }
              
              // User has interacted - this is a real change
              setIsDirty(true);
              
              // Push to history for undo/redo
              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(changes);
              
              // Limit history to MAX_HISTORY items
              if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              } else {
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              }
            }}
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      <ImportBudgetDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImport}
        existingRoles={currentBudgetData.resourceBudget.byRole}
        currentYear={parseInt(selectedYear)}
      />

      {/* Export Dialog */}
      <BudgetExportModal
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        budgetData={currentBudgetData}
        resourceOnly={true}
      />

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
